import React from 'react';
import { Button, IconButton, Input, InputGroup, Stack, Tag } from 'rsuite';
import { Icon } from '@rsuite/icons';
import { MdAdd, MdDelete, MdLink } from 'react-icons/md';
import { EventLink, EventLinkTypeList, eventLinkTypeFromUrl } from '@/models/event-link.model';

interface Props {
  value?: EventLink[];
  onChange?: (links: EventLink[]) => void;
  readOnly?: boolean;
  disabled?: boolean;
}

// Official pages of an event: one row per link (type, title, URL). The type follows the URL.
const EventLinksEditor: React.FC<Props> = ({ value, onChange, readOnly, disabled }) => {
  const links = value ?? [];
  const update = (i: number, patch: Partial<EventLink>) =>
    onChange?.(links.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const typeLabel = (url: string) =>
    EventLinkTypeList.find(t => t.value === eventLinkTypeFromUrl(url))?.label ?? 'Web';

  if (readOnly && links.length === 0) return <div style={{ color: 'var(--rs-text-secondary)' }}>No links</div>;

  return (
    <Stack direction="column" alignItems="stretch" spacing={6}>
      {links.map((l, i) => (
        <Stack key={i} spacing={6} alignItems="center">
          <Tag style={{ width: 80, textAlign: 'center' }}>{typeLabel(l.url)}</Tag>
          <Input style={{ width: 160 }} placeholder="Title" value={l.title ?? ''}
            readOnly={readOnly} disabled={disabled}
            onChange={v => update(i, { title: v })} />
          <Stack.Item grow={1}>
            <InputGroup inside>
              <InputGroup.Button onClick={() => l.url && window.open(l.url, '_blank')}>
                <Icon as={MdLink} />
              </InputGroup.Button>
              <Input placeholder="https://…" value={l.url ?? ''}
                readOnly={readOnly} disabled={disabled}
                onChange={v => update(i, { url: v })} />
            </InputGroup>
          </Stack.Item>
          {!readOnly && <IconButton icon={<Icon as={MdDelete} />} appearance="subtle" disabled={disabled}
            onClick={() => onChange?.(links.filter((_, j) => j !== i))} />}
        </Stack>
      ))}
      {!readOnly && (
        <div>
          <Button size="sm" startIcon={<Icon as={MdAdd} />} disabled={disabled}
            onClick={() => onChange?.([...links, { url: '' }])}>
            Add link
          </Button>
        </div>
      )}
    </Stack>
  );
};

export default EventLinksEditor;
